import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CATALOG_CATEGORIES } from "@/data/catalog";
import { call, errText } from "@/lib/api";

const EMPTY = { name: "", manufacturer: "", year: 1988, category: "figuras", line: "", version: "", description: "", specs: [], owned: false };
const specsToText = (specs) => (specs ?? []).map((s) => `${s.label}: ${s.value}`).join("\n");
const textToSpecs = (t) => t.split("\n").map((l) => l.split(":")).filter((p) => p.length > 1 && p[0].trim() && p.slice(1).join(":").trim()).map((p) => ({ label: p[0].trim(), value: p.slice(1).join(":").trim() }));

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mono mb-1 block text-[10px] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

export const ItemForm = ({ item, onClose, onSaved }) => {
  const [v, setV] = useState(() => ({ ...EMPTY, ...(item?.id ? item : {}) }));
  const [specs, setSpecs] = useState(() => specsToText(item?.specs));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const body = { name: v.name, manufacturer: v.manufacturer, year: Number(v.year), category: v.category, line: v.line, version: v.version, description: v.description, specs: textToSpecs(specs), owned: v.owned };
    const res = await call(item?.id ? `/admin/catalog/items/${item.id}` : "/admin/catalog/items", { method: item?.id ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) return setError(errText(data?.detail));
    onSaved(data);
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-white/10 bg-[#0d0c0a] text-[#efe8dc]" data-lenis-prevent data-testid="item-form-dialog">
        <DialogTitle className="font-display text-3xl font-normal">{item?.id ? "Editar pieza" : "Nueva pieza"}</DialogTitle>
        <form onSubmit={save} className="mt-2 grid gap-4 sm:grid-cols-2" data-testid="item-form">
          <div className="sm:col-span-2"><Field label="Nombre"><input required value={v.name} onChange={set("name")} className="support-input" data-testid="item-form-name" /></Field></div>
          <Field label="Fabricante / editorial"><input required value={v.manufacturer} onChange={set("manufacturer")} className="support-input" data-testid="item-form-manufacturer" /></Field>
          <Field label="Año"><input required type="number" min={1984} max={2035} value={v.year} onChange={set("year")} className="support-input" data-testid="item-form-year" /></Field>
          <Field label="Categoría">
            <select value={v.category} onChange={set("category")} className="support-input" data-testid="item-form-category">
              {CATALOG_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Línea o serie"><input value={v.line} onChange={set("line")} className="support-input" data-testid="item-form-line" /></Field>
          <div className="sm:col-span-2"><Field label="Versión o variante"><input value={v.version} onChange={set("version")} className="support-input" data-testid="item-form-version" /></Field></div>
          <div className="sm:col-span-2"><Field label="Descripción"><textarea rows={3} value={v.description} onChange={set("description")} className="support-input" data-testid="item-form-description" /></Field></div>
          <div className="sm:col-span-2"><Field label="Ficha técnica (una por línea: Etiqueta: valor)"><textarea rows={4} value={specs} onChange={(e) => setSpecs(e.target.value)} placeholder={"Escala: 4 pulgadas\nAccesorios: Katanas"} className="support-input" data-testid="item-form-specs" /></Field></div>
          <label className="flex items-center gap-3 text-sm sm:col-span-2">
            <input type="checkbox" checked={v.owned} onChange={set("owned")} className="h-4 w-4 accent-[#7bc74d]" data-testid="item-form-owned" /> La tengo en la guarida
          </label>
          {error && <p className="text-sm text-[#ff8a7a] sm:col-span-2" data-testid="item-form-error">{error}</p>}
          <div className="flex gap-3 sm:col-span-2">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="item-form-save">{busy && <Loader2 size={16} className="animate-spin" />} Guardar</button>
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="item-form-cancel">Cancelar</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
